import React from "react";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
} from "@mui/material";

interface ExperienceItem {
  company: string;
  role: string;
  period: string;
  location: string;
  current?: boolean;
  points: string[];
}

interface EducationItem {
  institution: string;
  role: string;
  period: string;
}

const experiences: ExperienceItem[] = [
  {
    company: "Xcenter Technologies",
    role: "Lead Flutter Developer",
    period: "June 2023 – June 2026",
    location: "Kathmandu, Nepal",
    current: false,
    points: [
      "Led and mentored a team of Flutter developers, fostering a high-performance and collaborative engineering culture.",
      "Architected scalable, maintainable mobile applications using Clean Architecture principles.",
      "Managed the full software development lifecycle, including planning, estimation, task prioritization, and on-time delivery of features.",
      "Conducted regular code reviews and provided constructive feedback to improve team skill levels.",
      "Optimized app performance, memory usage, and responsiveness across Android and iOS platforms.",
      "Integrated third-party services including Firebase, REST APIs, real-time communication, and payment systems.",
    ],
  },
  {
    company: "Groov Interactive",
    role: "Head of Product",
    period: "Sept 2024 – June 2026",
    location: "Dubai, United Arab Emirates (Remote)",
    points: [
      "Led end-to-end product development from concept to launch.",
      "Defined product vision, roadmap, and feature priorities.",
      "Managed cross-functional teams and coordinated execution.",
      "Oversaw sprint planning, delivery timelines, and quality control.",
    ],
  },
  {
    company: "Frits Go",
    role: "Flutter Developer",
    period: "July 2019 – Feb 2023",
    location: "Kathmandu, Nepal",
    points: [
      "Joined as a fresher with basic Flutter knowledge and learned most things hands-on while working on real projects.",
      "Contributed to and maintained a production-level Flutter application used by real users, ensuring stability, performance, and timely feature releases.",
      "Gained hands-on experience across the full software development lifecycle—from initial planning and architecture discussions to mobile development, backend integration, web support, and final production deployment.",
    ],
  },
];

const education: EducationItem[] = [
  {
    institution: "Himalaya College, IMS, KIST, MounTech",
    role: "Flutter & Python Trainer",
    period: "2019 – 2022",
  },
  {
    institution: "Tribhuvan University, Nepal",
    role: "Bachelor's Degree in Computer Science",
    period: "2016 – 2020",
  },
];

const Experience: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, mb: 4, textAlign: "center" }}
      >
        Experience
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {experiences.map((exp, index) => (
          <Card key={index} sx={{ height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 2,
                  flexWrap: "wrap",
                  mb: 1,
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {exp.company}
                    {exp.current && (
                      <Chip
                        label="Current"
                        size="small"
                        sx={{
                          ml: 1,
                          backgroundColor: "rgba(145, 189, 58, 0.15)",
                          color: "primary.main",
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Typography>
                  <Typography variant="subtitle1" color="primary.main">
                    {exp.role}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {exp.period}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {exp.location}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box component="ul" sx={{ m: 0, pl: 3 }}>
                {exp.points.map((point, idx) => (
                  <Typography
                    component="li"
                    key={idx}
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.75, lineHeight: 1.6 }}
                  >
                    {point}
                  </Typography>
                ))}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Typography
        variant="h5"
        sx={{ fontWeight: 700, mt: 6, mb: 3, textAlign: "center" }}
      >
        Training & Education
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {education.map((edu, index) => (
          <Card key={index}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {edu.institution}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {edu.role}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {edu.period}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Container>
  );
};

export default Experience;
